import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

interface TeamMemberProps {
  name: string;
  role: string;
  specialty: string;
  image: string;
  index: number;
}

const TeamMemberCard: React.FC<TeamMemberProps> = ({ name, role, specialty, image, index }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      className="group relative bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{ animationDelay: `${index * 150}ms` }}
    >
      {/* Image Container */}
      <div className="relative aspect-[3/4] overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200">
        <img 
          src={image} 
          alt={name}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
        />
        
        {/* Overlay on hover */}
        <div className={`absolute inset-0 bg-gradient-to-t from-dark/90 via-dark/50 to-transparent transition-opacity duration-500 ${isHovered ? 'opacity-100' : 'opacity-0'}`}>
          <div className="absolute bottom-0 left-0 right-0 p-6 transform transition-all duration-500">
            <Link
              to="/services"
              className="inline-flex items-center gap-2 text-white font-semibold hover:text-secondary transition-colors"
            >
              <span>Ver Perfil Completo</span>
              <svg className="w-5 h-5 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </div>
        </div>
        
        {/* Top decoration */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-secondary to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
      </div>

      {/* Info Container */}
      <div className="p-6 relative">
        {/* Decorative accent */}
        <div className="absolute top-0 left-6 w-12 h-1 bg-gradient-to-r from-secondary to-primary transform -translate-y-3"></div>
        
        {/* Name */}
        <h3 className="text-xl font-serif font-bold text-gray-800 mb-2 group-hover:text-secondary transition-colors duration-300">
          {name}
        </h3>
        
        {/* Role */}
        <p className="text-sm font-semibold text-secondary mb-2 uppercase tracking-wider">
          {role}
        </p>
        
        {/* Specialty */}
        <p className="text-sm text-gray-600 leading-relaxed mb-4">
          {specialty}
        </p>
        
        {/* Credentials Icons */}
        <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
          <div className="group/icon relative">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-secondary/10 to-primary/10 flex items-center justify-center transition-all duration-300 group-hover/icon:from-secondary group-hover/icon:to-primary">
              <svg className="w-4 h-4 text-secondary group-hover/icon:text-white transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
          <div className="group/icon relative">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-secondary/10 to-primary/10 flex items-center justify-center transition-all duration-300 group-hover/icon:from-secondary group-hover/icon:to-primary">
              <svg className="w-4 h-4 text-secondary group-hover/icon:text-white transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </div>
          </div>
          <div className="group/icon relative">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-secondary/10 to-primary/10 flex items-center justify-center transition-all duration-300 group-hover/icon:from-secondary group-hover/icon:to-primary">
              <svg className="w-4 h-4 text-secondary group-hover/icon:text-white transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Team: React.FC = () => {
  const { elementRef, isVisible } = useScrollAnimation();

  const teamMembers = [
    {
      name: 'Dr. Dieter B. C. de Medeiros',
      role: 'Especialista em Transplante Capilar',
      specialty: 'Membro da Sociedade Brasileira de Cirurgia de Restauração Capilar - Mais de 150 cirurgias realizadas',
      image: '/assets/img/prof/Dr%20Dieter%20Medeiros.png'
    },
    {
      name: 'Drª Arielly de Sousa Leitão',
      role: 'Especialista em Harmonização Facial',
      specialty: 'Pós-graduada em Harmonização Facial - Especialista em Rinomodelação',
      image: '/assets/img/prof/Drª%20Arielly%20foto.png'
    },
    {
      name: 'Dr. Arlley de Sousa Leitão',
      role: 'Cirurgião Implantodontista',
      specialty: 'Professor Mestre em Odontologia - 11 anos de experiência em cirurgias faciais',
      image: '/assets/img/prof/Dr%20Arlley.png'
    },
    {
      name: 'Drª Brenda Brasileira Medeiros',
      role: 'Cirurgiã Bucomaxilofacial',
      specialty: 'Membro da Academia Brasileira de Cirurgias Estéticas da Face e Harmonização Facial',
      image: '/assets/img/prof/Drª%20Brenda.png'
    }
  ];

  return (
    <section 
      ref={elementRef}
      className="py-20 md:py-24 bg-gradient-to-b from-white via-[#FCFCFD] to-gray-50 relative overflow-hidden"
    >
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-full h-full opacity-5 pointer-events-none">
        <div className="absolute top-20 right-10 w-96 h-96 bg-secondary rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-primary rounded-full blur-3xl"></div>
      </div>

      <div className="container-custom relative z-10">
        {/* Header */}
        <div className={`text-center mb-16 max-w-3xl mx-auto transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <span className="inline-block px-4 py-2 bg-secondary/10 text-secondary font-semibold text-sm rounded-full mb-4 uppercase tracking-wider">
            Nossa Equipe
          </span>
          <h2 className="text-4xl md:text-5xl font-serif font-bold text-gray-800 mb-6">
            Profissionais de <span className="text-secondary">Excelência</span>
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            Conheça nossa equipe multidisciplinar de especialistas altamente qualificados, dedicados a oferecer os melhores resultados em estética médica.
          </p>
        </div>

        {/* Team Grid */}
        <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12 transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          {teamMembers.map((member, index) => (
            <TeamMemberCard
              key={index}
              name={member.name}
              role={member.role}
              specialty={member.specialty}
              image={member.image}
              index={index}
            />
          ))}
        </div>

        {/* CTA */}
        <div className={`text-center transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <Link
            to="/services"
            className="group inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-secondary to-primary text-white font-semibold text-base rounded-full shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105 relative overflow-hidden"
          >
            <span className="absolute inset-0 bg-gradient-to-r from-primary to-secondary transform translate-x-full group-hover:translate-x-0 transition-transform duration-500"></span>
            <span className="relative z-10">Conheça Toda a Equipe</span>
            <svg 
              className="relative z-10 w-5 h-5 transform transition-transform duration-300 group-hover:translate-x-2" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default Team;
